import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DashboardLayout } from '../../Components/Layout/DashboardLayout';
import { toast } from 'react-toastify';
import {
  useGetLandmarksQuery,
  useGetPathsQuery,
  useCalculateRouteMutation
} from '../../Redux/Apis/Navigation';
import CustomButton from '../../Components/CustomButton';
import './style.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const RouteTester = () => {
  const { data: landmarksData } = useGetLandmarksQuery({});
  const { data: pathsData } = useGetPathsQuery({});
  const [calculateRoute, { isLoading }] = useCalculateRouteMutation();

  const [startLandmark, setStartLandmark] = useState('');
  const [endLandmark, setEndLandmark] = useState('');
  const [transport, setTransport] = useState('golf_cart');
  const [route, setRoute] = useState(null);

  const landmarks = landmarksData?.data?.landmarks || [];
  const paths = pathsData?.data?.paths || [];

  const defaultCenter = [31.9513, -81.0585];

  const handleCalculateRoute = async () => {
    if (!startLandmark || !endLandmark) {
      toast.error('Please select both start and destination');
      return;
    }

    if (startLandmark === endLandmark) {
      toast.error('Start and destination must be different');
      return;
    }

    const start = landmarks.find(l => l._id === startLandmark);
    const end = landmarks.find(l => l._id === endLandmark);

    if (!start || !end) {
      toast.error('Invalid landmarks selected');
      return;
    }

    try {
      const result = await calculateRoute({
        from: {
          longitude: start.location.coordinates[0],
          latitude: start.location.coordinates[1]
        },
        to: {
          longitude: end.location.coordinates[0],
          latitude: end.location.coordinates[1]
        },
        transport
      }).unwrap();

      setRoute(result.data.route);
      toast.success('Route calculated successfully');
    } catch (err) {
      toast.error(err.data?.message || 'Failed to calculate route');
      setRoute(null);
    }
  };

  const handleClear = () => {
    setStartLandmark('');
    setEndLandmark('');
    setRoute(null);
  };

  const getTransportLabel = (t) => {
    switch (t) {
      case 'golf_cart':
        return '🏌️ Golf Cart';
      case 'walking':
        return '🚶 Walking';
      case 'car':
        return '🚗 Car';
      default:
        return t;
    }
  };

  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1609.34).toFixed(2)} mi`;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getPathColor = (pathType) => {
    switch (pathType) {
      case 'cart_path':
        return '#4CAF50';
      case 'street':
        return '#2196F3';
      case 'mixed':
        return '#FF9800';
      default:
        return '#757575';
    }
  };

  return (
    <DashboardLayout>
      <div className="routeTesterContainer">
        <div className="routeTesterHeader">
          <h2>Route Tester</h2>
          <p>Test navigation routes before deploying to users</p>
        </div>

        <div className="routeTesterContent">
          <div className="controlPanel">
            <div className="formSection">
              <h3>Route Parameters</h3>

              <div className="formGroup">
                <label>Starting Point</label>
                <select
                  value={startLandmark}
                  onChange={(e) => setStartLandmark(e.target.value)}
                >
                  <option value="">Select start location...</option>
                  {landmarks.map(landmark => (
                    <option key={landmark._id} value={landmark._id}>
                      {landmark.name} ({landmark.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="formGroup">
                <label>Destination</label>
                <select
                  value={endLandmark}
                  onChange={(e) => setEndLandmark(e.target.value)}
                >
                  <option value="">Select destination...</option>
                  {landmarks.map(landmark => (
                    <option key={landmark._id} value={landmark._id}>
                      {landmark.name} ({landmark.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="formGroup">
                <label>Transport Mode</label>
                <div className="transportButtons">
                  <button
                    className={`transportBtn ${transport === 'golf_cart' ? 'active' : ''}`}
                    onClick={() => setTransport('golf_cart')}
                  >
                    🏌️ Golf Cart
                  </button>
                  <button
                    className={`transportBtn ${transport === 'walking' ? 'active' : ''}`}
                    onClick={() => setTransport('walking')}
                  >
                    🚶 Walking
                  </button>
                  <button
                    className={`transportBtn ${transport === 'car' ? 'active' : ''}`}
                    onClick={() => setTransport('car')}
                  >
                    🚗 Car
                  </button>
                </div>
              </div>

              <div className="actionButtons">
                <CustomButton
                  text="Calculate Route"
                  onClick={handleCalculateRoute}
                  variant="primaryButton"
                  disabled={isLoading || !startLandmark || !endLandmark}
                />
                <CustomButton
                  text="Clear"
                  onClick={handleClear}
                  variant="secondaryButton"
                />
              </div>
            </div>

            {route && (
              <div className="resultsSection">
                <h3>Route Results</h3>
                
                <div className="routeSummary">
                  <div className="summaryItem">
                    <span className="label">Distance:</span>
                    <span className="value">{formatDistance(route.summary.distance)}</span>
                  </div>
                  <div className="summaryItem">
                    <span className="label">Duration:</span>
                    <span className="value">{formatDuration(route.summary.duration)}</span>
                  </div>
                  <div className="summaryItem">
                    <span className="label">Transport:</span>
                    <span className="value">{getTransportLabel(route.summary.transport)}</span>
                  </div>
                </div>

                {route.snapping && (
                  <div className="snappingInfo">
                    <p><strong>Snapping Info:</strong></p>
                    <p>Start offset: {formatDistance(route.snapping.startOffset)}</p>
                    <p>End offset: {formatDistance(route.snapping.endOffset)}</p>
                  </div>
                )}

                <div className="instructionsList">
                  <h4>Turn-by-Turn Instructions</h4>
                  {route.instructions.map((instruction, index) => (
                    <div key={index} className="instructionItem">
                      <div className="instructionNumber">{index + 1}</div>
                      <div className="instructionDetails">
                        <div className="instructionText">{instruction.text}</div>
                        <div className="instructionMeta">
                          {instruction.distance > 0 && (
                            <span>{formatDistance(instruction.distance)}</span>
                          )}
                          <span className="instructionType">{instruction.type}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mapPanel">
            <MapContainer
              center={defaultCenter}
              zoom={15}
              style={{ height: '700px', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {/* Existing paths */}
              {paths.map((path) => {
                const positions = path.geometry.coordinates.map(coord => [coord[1], coord[0]]);
                return (
                  <Polyline
                    key={path._id}
                    positions={positions}
                    color={getPathColor(path.pathType)}
                    weight={3}
                    opacity={0.4}
                  />
                );
              })}

              {/* All landmarks */}
              {landmarks.map((landmark) => {
                const position = [landmark.location.coordinates[1], landmark.location.coordinates[0]];
                const isStart = landmark._id === startLandmark;
                const isEnd = landmark._id === endLandmark;
                
                return (
                  <Marker key={landmark._id} position={position}>
                    <Popup>
                      <div>
                        <h4>{landmark.name}</h4>
                        {isStart && <p><strong>🟢 Start</strong></p>}
                        {isEnd && <p><strong>🔴 Destination</strong></p>}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* Calculated route */}
              {route && route.path && (
                <>
                  <Polyline
                    positions={route.path.map(coord => [coord[1], coord[0]])}
                    color="#FF5722"
                    weight={6}
                    opacity={0.9}
                  />
                  
                  {/* Show snapping lines */}
                  {route.snapping && (
                    <>
                      <Polyline
                        positions={[
                          [route.snapping.startCoordinate[1], route.snapping.startCoordinate[0]],
                          [route.snapping.snappedStartCoordinate[1], route.snapping.snappedStartCoordinate[0]]
                        ]}
                        color="#9C27B0"
                        weight={3}
                        dashArray="5, 10"
                      />
                      <Polyline
                        positions={[
                          [route.snapping.snappedEndCoordinate[1], route.snapping.snappedEndCoordinate[0]],
                          [route.snapping.endCoordinate[1], route.snapping.endCoordinate[0]]
                        ]}
                        color="#9C27B0"
                        weight={3}
                        dashArray="5, 10"
                      />
                    </>
                  )}
                </>
              )}
            </MapContainer>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RouteTester;
